import React, { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { showInkToast } from '../cms/InkToast';
import { ApprovalChip } from './AgentControls';
import { agentAnchor, approvalsAt, GOES_OUT_WITHOUT_YOU, GoesOutAction } from './goesOut';
import type { AccountAgent, AgentRule, AgentRules as Rules } from './accountAgents';

/**
 * Account → AI Agents: what one agent can do (UX optimization review, 2026-10-24;
 * backlog 594cb5eb). "Can it do this?" is access and stays here; whether it asks
 * you first is a chip that opens Account → Approvals. The server checks these on
 * every call the agent makes (apiserver platform/services/agentPermissions.js).
 * Stored as allow (goes out without asking), ask (ask me first) or never (can't).
 * Admins change them; everyone else sees them.
 */

export const SET_AGENT_PERMISSION = gql`
  mutation SetAgentPermission($accountId: ID!, $dataSourceId: ID!, $key: String!, $value: String!) {
    setAgentPermission(accountId: $accountId, dataSourceId: $dataSourceId, key: $key, value: $value)
  }
`;

const ROWS: Array<{
  key: keyof Rules;
  name: string;
  desc: string;
  /** What the selected choice means, where the choices differ in kind. */
  descFor?: Partial<Record<AgentRule, string>>;
}> = [
  {
    key: 'drafts',
    name: 'Create & edit drafts',
    desc: 'Articles, pages, forms, media, social posts',
  },
  {
    key: 'publish',
    name: 'Publish & schedule',
    desc: 'The copy edit runs first',
    // The helper names the setting that's selected (backlog e52b11b8).
    descFor: {
      allow: 'Publishes after you OK that version in the chat, with no check in tinycms',
      ask: 'You approve in tinycms, not in the chat',
      never: 'Only a person publishes or schedules, in tinycms',
    },
  },
  {
    key: 'surface',
    name: 'Change the site surface',
    desc: 'The site’s look, header, footer and home page',
  },
  { key: 'delete', name: 'Delete content', desc: 'Unpublishing is always allowed' },
];

export function AgentRules({
  accountId,
  agent,
  onChanged,
}: {
  accountId: string;
  agent: Pick<AccountAgent, 'name' | 'dataSourceId' | 'rules' | 'rulesEditable'>;
  onChanged: () => void;
}) {
  const [save] = useMutation(SET_AGENT_PERMISSION, { refetchQueries: [GOES_OUT_WITHOUT_YOU] });
  // The choice shows at once; the refetch confirms it.
  const [pending, setPending] = useState<Partial<Rules>>({});
  const rules = { ...agent.rules, ...pending };

  const pick = async (key: keyof Rules, can: boolean, rowName: string) => {
    const current = rules[key];
    if (!agent.rulesEditable || (current !== 'never') === can) return;
    // Back on: Ask me first. Drafts have no approval, so they're simply allowed.
    const value: AgentRule = !can ? 'never' : key === 'drafts' ? 'allow' : 'ask';
    setPending((p) => ({ ...p, [key]: value }));
    try {
      await save({
        variables: { accountId, dataSourceId: agent.dataSourceId, key, value },
      });
      onChanged();
      showInkToast(`${agent.name}: ${can ? 'can' : 'can’t'} ${rowName.toLowerCase()}.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save. Try again.');
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  };

  return (
    <div className="agr">
      <div className="agr-row agr-heads" aria-hidden>
        <span>Action</span>
        <span>Approval</span>
        <span>Can it do this?</span>
      </div>
      {ROWS.map((row) => {
        const value = rules[row.key];
        const can = value !== 'never';
        const outgoing = row.key !== 'drafts';
        return (
          <div className="agr-row" key={row.key}>
            <div className="agr-text">
              <div className="agr-name">{row.name}</div>
              <div className="agr-desc">{row.descFor?.[value] || row.desc}</div>
            </div>
            <div className="agr-approval">
              {!can ? (
                <span className="agc-chip" data-static="true" data-cant="true">
                  <span className="ink-dot agc-chip-dot" aria-hidden />
                  Can’t do this
                </span>
              ) : outgoing ? (
                <ApprovalChip
                  mode={value === 'allow' ? 'out' : 'ask'}
                  to={approvalsAt(agentAnchor(agent.dataSourceId, row.key as GoesOutAction))}
                />
              ) : null}
            </div>
            <div className="agr-seg" role="radiogroup" aria-label={`Can it: ${row.name}`}>
              {[
                [true, 'Can'],
                [false, 'Can’t'],
              ].map(([option, label]) => (
                <button
                  type="button"
                  key={String(label)}
                  role="radio"
                  aria-checked={can === option}
                  data-on={can === option ? 'true' : undefined}
                  disabled={!agent.rulesEditable}
                  onClick={() => void pick(row.key, Boolean(option), row.name)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
